import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Lock, MessageCircle, Search, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const FEATURE_ICONS = {
  search: Search,
  messaging: MessageCircle,
  chat: MessageCircle,
  feed: Users,
  default: Lock
};

const FEATURE_MESSAGES = {
  search: {
    title: 'Recherche avancée',
    description: 'Accédez à la recherche avancée pour trouver le profil idéal selon vos critères.'
  },
  messaging: {
    title: 'Messagerie privée',
    description: 'Envoyez et recevez des messages privés pour des échanges discrets.'
  },
  chat: {
    title: 'Chat en direct',
    description: 'Discutez en temps réel avec les membres de la communauté.'
  },
  feed: {
    title: 'Fil d\'actualité complet',
    description: 'Accédez à l\'ensemble du fil d\'actualité sans restriction.'
  },
  default: {
    title: 'Fonctionnalité réservée',
    description: 'Accédez à toutes les fonctionnalités de DiscreetLux.'
  }
};

export default function SubscriptionRequired({ open, onClose, feature = 'default' }) {
  const Icon = FEATURE_ICONS[feature] || FEATURE_ICONS.default;
  const message = FEATURE_MESSAGES[feature] || FEATURE_MESSAGES.default;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-dark-paper border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-gold text-center flex items-center justify-center gap-2">
            <Crown className="w-6 h-6" />
            Abonnement requis
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-6 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
            <Icon className="w-10 h-10 text-gold" />
          </div>
          
          <h3 className="text-white text-lg font-semibold mb-2">{message.title}</h3>
          <p className="text-gray-400 mb-6">{message.description}</p>
          
          <div className="glass-card p-4 mb-6">
            <p className="text-sm text-gray-300">
              En vous abonnant, vous débloquez :
            </p>
            <ul className="text-left mt-3 space-y-2">
              <li className="flex items-center gap-2 text-sm text-gray-400">
                <Search className="w-4 h-4 text-gold" />
                Recherche avancée illimitée
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-400">
                <MessageCircle className="w-4 h-4 text-gold" />
                Messagerie et chat illimités
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-400">
                <Users className="w-4 h-4 text-gold" />
                Fil d'actualité complet
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-400">
                <Crown className="w-4 h-4 text-gold" />
                Accès à tous les profils
              </li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-3">
            <Link to="/subscription" onClick={onClose}>
              <Button className="w-full btn-gold">
                <Crown className="w-4 h-4 mr-2" />
                Voir les abonnements
              </Button>
            </Link>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              Plus tard
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to check feature access and show popup
export function useFeatureAccess() {
  const [showPopup, setShowPopup] = React.useState(false);
  const [blockedFeature, setBlockedFeature] = React.useState('default');

  const checkAccess = (user, feature) => {
    if (!user) return false;
    
    const permissions = user.permissions || {};
    
    switch (feature) {
      case 'search':
        return permissions.can_search;
      case 'messaging':
        return permissions.can_message;
      case 'chat':
        return permissions.can_chat;
      case 'feed':
        return permissions.feed_unlimited;
      default:
        return user.is_subscribed;
    }
  };

  const requireAccess = (user, feature) => {
    if (!checkAccess(user, feature)) {
      setBlockedFeature(feature);
      setShowPopup(true);
      return false;
    }
    return true;
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  return {
    showPopup,
    blockedFeature,
    checkAccess,
    requireAccess,
    closePopup,
    SubscriptionPopup: () => (
      <SubscriptionRequired 
        open={showPopup} 
        onClose={closePopup} 
        feature={blockedFeature} 
      />
    )
  };
}
